/**
 * Configuração de navegação do AppShell — Design System v2 [UI-F0].
 *
 * Lista canônica dos itens da sidebar para os dois shells:
 *   - `SUPER_ADMIN_NAV` — painel `/admin/*`.
 *   - `TENANT_NAV` — área do tenant (`/dashboard`, `/minha-conta/*`,
 *     `/settings/*`, FAQ).
 *
 * Cada item é uma rota folha OU um grupo com `children[]` (accordion na
 * sidebar aberta, flyout na colapsada — ver AdminLayout/TenantLayout).
 * O `path` do item folha é usado para navegação E para marcar "ativo"
 * via comparação prefixada em `useLocation()`.
 *
 * Sobre ícones: usamos `@tabler/icons-react` que JÁ está nas dependências
 * do projeto. O prompt original pedia `lucide-react`, mas instalar uma
 * nova dependência ia além do escopo de Fase 0 (e a regra "não instalar
 * novas dependências"). API é equivalente — `IconX` é um React component.
 */

import {
  IconAddressBook,
  IconCalculator,
  IconAlertTriangle,
  IconBell,
  IconBuildingStore,
  IconCalendar,
  IconChartBar,
  IconClipboardList,
  IconFileDescription,
  IconFileInvoice,
  IconGauge,
  IconHeadset,
  IconHelpCircle,
  IconLayoutDashboard,
  IconLifebuoy,
  IconListDetails,
  IconMail,
  IconMessageCircle,
  IconMessageDots,
  IconPackage,
  IconSettings,
  IconShieldLock,
  IconStack2,
  IconUserCircle,
  IconUsers,
  IconUserShield,
  type Icon as TablerIcon,
  IconWallet,
} from '@tabler/icons-react';
import { leituraDaTela } from './telas-acesso';

export interface NavSubItem {
  key: string;
  label: string;
  path: string;
  /** Badge numérico (notificações, contagens). */
  pill?: number;
  /**
   * [AB#174] Código do módulo que esconde este subitem quando o tenant
   * não tem acesso (ex: 'mod_05'). Subitens sem `modulo` são sempre
   * visíveis — comportamento atual preservado.
   */
  modulo?: string;
  /**
   * [F4.3 #890 / F4.4 #890 / ADR-069 §13.27] Código (ou lista — basta ter uma) de permissão
   * RBAC que libera este subitem, via `usePermissao().pode()`. Subitem sem `permissao` é
   * sempre visível (mesma filosofia do `modulo`: opt-in, back-compat). Usuário sem NENHUMA
   * atribuição RBAC não vê nenhum subitem com `permissao` — `pode()` nega por padrão
   * (fail-closed) desde §13.27; só os itens de `TELAS_GLOBAIS` (sem `permissao`) continuam
   * visíveis a todo mundo.
   *
   * [F4.4] Sempre preenchido via `leituraDaTela('chave')` de `telas-acesso.ts` — nunca
   * literal aqui. `telas-acesso.ts` é a fonte única; este arquivo só referencia por chave.
   */
  permissao?: string | string[];
  /** Abas da tela (`?aba=`), na ordem; a primeira é o padrão. Só para a trilha (breadcrumb) — permissões ficam na própria tela. */
  abas?: Array<{ id: string; label: string }>;
}

/**
 * Submenu colapsável dentro de um grupo (ex.: Financeiro › Contas a receber). Não tem rota nem permissão própria: some
 * quando nenhum de seus `itens` é liberado. Abre sozinho quando a rota ativa está dentro dele.
 */
export interface NavSubGrupo {
  key: string;
  label: string;
  itens: NavSubItem[];
}

export type NavFilho = NavSubItem | NavSubGrupo;

export function ehSubGrupo(f: NavFilho): f is NavSubGrupo {
  return 'itens' in f;
}

/** Folhas (itens com rota) de um grupo, achatando os submenus. */
export function folhasDoGrupo(filhos: NavFilho[] | undefined): NavSubItem[] {
  return (filhos ?? []).flatMap((f) => (ehSubGrupo(f) ? f.itens : [f]));
}

export interface NavItem {
  key: string;
  label: string;
  icon: TablerIcon;
  /** Rota da folha. Quando há `children`, ignora-se em favor de toggle do accordion. */
  path?: string;
  pill?: number;
  children?: NavFilho[];
  /**
   * [AB#174] Código do módulo que esconde este item (e seus children)
   * quando o tenant não tem acesso (ex: 'mod_05'). Itens sem `modulo`
   * são sempre visíveis.
   *
   * Quando um item-grupo (com children) sobrevive ao filtro mas
   * todos os children foram escondidos por seus próprios `modulo`,
   * o item-grupo também desaparece (sem filhos → sem propósito).
   */
  modulo?: string;
  /** [F4.3 #890] Mesma regra do `NavSubItem.permissao`, pra itens-folha (sem `children`). */
  permissao?: string | string[];
}

/**
 * Painel do Super Admin — `/admin/*`. Sub-rotas que existem mas não
 * têm sentido na sidebar (impersonate, auditoria interna do dev) ficam
 * de fora. Sub-rotas como `/admin/tenants/:id` ficam acessíveis via
 * deep-link e não na sidebar.
 */
export const SUPER_ADMIN_NAV: NavItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: IconLayoutDashboard,
    path: '/admin/dashboard',
  },
  {
    key: 'tenants',
    label: 'Tenants',
    icon: IconUsers,
    path: '/admin/tenants',
  },
  {
    key: 'financeiro',
    label: 'Financeiro',
    icon: IconChartBar,
    children: [
      { key: 'fin-ops', label: 'Operações', path: '/admin/financeiro/operacoes' },
      { key: 'fin-inad', label: 'Inadimplência', path: '/admin/inadimplencia' },
      { key: 'fin-planos', label: 'Planos', path: '/admin/planos' },
    ],
  },
  {
    key: 'comunicacao',
    label: 'Comunicação',
    icon: IconMail,
    children: [
      { key: 'com-coms', label: 'Comunicados', path: '/admin/comunicados' },
      { key: 'com-tpl', label: 'Templates', path: '/admin/templates-mensagem' },
      { key: 'com-rn', label: 'Release notes', path: '/admin/release-notes' },
    ],
  },
  {
    key: 'suporte',
    label: 'Suporte',
    icon: IconLifebuoy,
    children: [
      { key: 'sup-cha', label: 'Chamados', path: '/admin/chamados' },
      { key: 'sup-faq', label: 'FAQ', path: '/admin/faq' },
    ],
  },
  {
    key: 'sistema',
    label: 'Sistema',
    icon: IconSettings,
    children: [
      { key: 'sis-cfg', label: 'Configurações', path: '/admin/system-settings' },
      { key: 'sis-fat', label: 'Faturamento Vinya', path: '/admin/faturamento-vinya' },
      { key: 'sis-mod', label: 'Módulos', path: '/admin/modulos' },
      { key: 'sis-doc', label: 'Documentos legais', path: '/admin/documentos-legais' },
      { key: 'sis-seg', label: 'Segmentos', path: '/admin/segmentos' },
      { key: 'sis-mon', label: 'Monitoramento', path: '/admin/sistema/monitoramento' },
    ],
  },
  {
    key: 'seguranca',
    label: 'Segurança',
    icon: IconShieldLock,
    children: [
      { key: 'sec-imp', label: 'Impersonate', path: '/admin/impersonate' },
      { key: 'sec-aud', label: 'Auditoria', path: '/admin/audit-log' },
    ],
  },
];

/**
 * Área do tenant logado (ADMIN_TENANT / GESTOR / ATENDENTE etc.).
 */
export const TENANT_NAV: NavItem[] = [
  {
    key: 'dashboard',
    label: 'Início',
    icon: IconGauge,
    path: '/dashboard',
  },
  {
    // [MOD-03 AB#112] Sprint 1.3 — Comercial entrou antes de Configurações:
    // hierarquia de uso (atendimento > config). Inbox é a primeira folha;
    // CRM/Campanhas/IA virão em Sprints posteriores (REQ-015/016/014).
    key: 'comercial',
    label: 'Comercial',
    icon: IconMessageCircle,
    // [AB#492] Sem esta linha o item aparecia para QUALQUER tenant — o filtro do
    // AppShell é permissivo por desenho (`!item.modulo || tem(item.modulo)`), e
    // item sem `modulo` é sempre visível. Um tenant ATIVO sem mod_03 via o menu
    // inteiro e levava 403 ao clicar: menu que oferece porta que não abre.
    // O gate aqui cobre os três filhos, já que grupo sem filhos visíveis some.
    modulo: 'mod_03',
    children: [
      { key: 'com-inbox', label: 'Inbox', path: '/comercial/inbox', permissao: leituraDaTela('com-inbox') },
      { key: 'com-crm', label: 'CRM', path: '/comercial/crm', permissao: leituraDaTela('com-crm') },
      // [MOD-03 AB#230] Campanhas — disparo em massa. Gating mod_03 no backend.
      { key: 'com-cmp', label: 'Campanhas', path: '/comercial/campanhas', permissao: leituraDaTela('com-cmp') },
    ],
  },
  {
    // [MOD-04 AB#169 / ADR-029 Rev.2] Agenda — visão de compromissos.
    // Aparece só quando o tenant tem mod_04. Por enquanto folha simples;
    // sub-itens (Atendimentos, Configurações da Agenda) entram em fases
    // posteriores como children sob o mesmo gate.
    key: 'agenda',
    label: 'Agenda',
    icon: IconCalendar,
    modulo: 'mod_04',
    path: '/agenda',
    permissao: leituraDaTela('agenda'),
  },
  {
    // [MOD-05 AB#174 / MOD-07 AB#300 / AB#307] Catálogo — só aparece quando o
    // tenant tem mod_05. Itens e Categorias são tabs da CatalogoPage (/catalogo);
    // Fornecedores tem página própria; Importar é o bulk loader (AB#306). A rota
    // legada /vendas/catalogo redireciona para /catalogo (App.tsx).
    key: 'catalogo',
    label: 'Catálogo',
    icon: IconClipboardList,
    modulo: 'mod_05',
    children: [
      { key: 'cat-itens', label: 'Itens & Categorias', path: '/catalogo', permissao: leituraDaTela('cat-itens') },
      // [F4.4 #890] F4.3 reusava catalogo.item:ler por não achar recurso dedicado — o
      // levantamento da F4.4 achou: fornecedor é `parte`, não item de catálogo.
      { key: 'cat-fornecedores', label: 'Fornecedores', path: '/catalogo/fornecedores', permissao: leituraDaTela('cat-fornecedores') },
      // [AB#307 ADENDO 1] Importar deixou de ser página separada — virou modal
      // contextual dentro das tabs Itens e Fornecedores.
    ],
  },
  {
    // [MOD-FIN AB#645] Financeiro — só aparece quando o tenant tem mod_07,
    // mesmo mecanismo do Catálogo (mod_05) e da Agenda (mod_04): o AppShell
    // filtra por `!item.modulo || tem(item.modulo)`. Enquanto mod_07 estiver
    // `ativo: false` no catálogo, nenhum tenant tem — e o grupo não aparece
    // para ninguém, sem precisar de flag separada.
    key: 'financeiro',
    label: 'Financeiro',
    icon: IconWallet,
    modulo: 'mod_07',
    children: [
      // [Menu por ciclo] Contas a receber / Contas a pagar são submenus colapsáveis; o resto são itens diretos. Mesmas telas e
      // permissões de antes. [AB#648] Títulos = mesma página nas duas direções (direção na query).
      // [F4.4 #890] A rota de títulos é liberada com QUALQUER uma das duas leituras (ver App.tsx); o item de menu só aparece
      // pra quem tem a leitura DESTA direção.
      {
        key: 'fin-grp-receber',
        label: 'Contas a receber',
        itens: [
          { key: 'fin-receber', label: 'Títulos', path: '/financeiro/titulos?direcao=RECEBER', permissao: leituraDaTela('fin-receber') },
          // [ADR-074 Adendo 3 / AB#1107] Módulo próprio (fin.cobranca). Uma tela com abas: Boletos e Pix | Lotes | Regra automática | Retornos.
          {
            key: 'fin-cobranca',
            label: 'Cobrança',
            path: '/financeiro/cobranca',
            permissao: leituraDaTela('fin-cobranca'),
            abas: [
              { id: 'boletos', label: 'Boletos e Pix' },
              { id: 'lotes', label: 'Lotes' },
              { id: 'regra', label: 'Regra automática' },
              { id: 'retornos', label: 'Retornos' },
            ],
          },
        ],
      },
      {
        key: 'fin-grp-pagar',
        label: 'Contas a pagar',
        itens: [
          { key: 'fin-pagar', label: 'Títulos', path: '/financeiro/titulos?direcao=PAGAR', permissao: leituraDaTela('fin-pagar') },
          // Uma tela com abas: Agenda | Lotes. Aparece para quem tem qualquer uma das duas leituras.
          {
            key: 'fin-pagamentos',
            label: 'Pagamentos',
            path: '/financeiro/pagamentos',
            permissao: [...leituraDaTela('fin-agenda-pagamentos'), ...leituraDaTela('fin-remessas-pagamento')],
            abas: [
              { id: 'agenda', label: 'Agenda' },
              { id: 'lotes', label: 'Lotes' },
            ],
          },
        ],
      },
      { key: 'fin-contas', label: 'Contas bancárias', path: '/financeiro/contas', permissao: leituraDaTela('fin-contas') },
      { key: 'fin-conciliacao', label: 'Conciliação', path: '/financeiro/conciliacao', permissao: leituraDaTela('fin-conciliacao') },
      { key: 'fin-centros', label: 'Centro de custo', path: '/financeiro/centro-de-custo', permissao: leituraDaTela('fin-centros') },
    ],
  },
  {
    // [US 974 UI, decisão do Filipe] Contabilidade é menu próprio (ADR-061 D1). Itens desta entrega:
    // Plano de contas (migrado do Financeiro, mesma chave de permissão) e Fechamento fiscal.
    key: 'contabilidade',
    label: 'Contabilidade',
    icon: IconCalculator,
    modulo: 'mod_07',
    children: [
      { key: 'fin-planos', label: 'Plano de contas', path: '/contabilidade/plano-de-contas', permissao: leituraDaTela('fin-planos') },
      // [ADR-061 US8.2] Demonstrativos (leitura) logo abaixo do Plano de contas — mod_09.
      { key: 'cont-balancete', label: 'Balancete', path: '/contabilidade/balancete', modulo: 'mod_09', permissao: leituraDaTela('cont-balancete') },
      { key: 'cont-razao', label: 'Razão', path: '/contabilidade/razao', modulo: 'mod_09', permissao: leituraDaTela('cont-razao') },
      // [ADR-061 AB#1046] Diário — mesmo gating de Razão/Balancete (mod_09).
      { key: 'cont-diario', label: 'Diário', path: '/contabilidade/diario', modulo: 'mod_09', permissao: leituraDaTela('cont-diario') },
      { key: 'cont-dre', label: 'DRE', path: '/contabilidade/dre', modulo: 'mod_09', permissao: leituraDaTela('cont-dre') },
      { key: 'cont-fech-fiscal', label: 'Fechamento de períodos', path: '/contabilidade/fechamento-fiscal', permissao: leituraDaTela('cont-fech-fiscal') },
      { key: 'cont-parametros', label: 'Parâmetros', path: '/contabilidade/parametros', permissao: leituraDaTela('cont-parametros') },
      // [ADR-061 F1 US1/US3] Primeiro item contábil de verdade do grupo — gating por
      // mod_09 (os 3 acima seguem mod_07, sem mudança).
      { key: 'cont-plano-contabil', label: 'Plano contábil', path: '/contabilidade/plano-contabil', modulo: 'mod_09', permissao: leituraDaTela('cont-plano-contabil') },
      // [ADR-061 D2/D6, US5] Mesmo gating de "Plano contábil" — mod_09.
      // [ADR-061 US8.4.2] Estrutura da DRE (linhas + vínculo conta→linha) — mod_09.
      { key: 'cont-dre-estrutura', label: 'Estrutura da DRE', path: '/contabilidade/dre/estrutura', modulo: 'mod_09', permissao: leituraDaTela('cont-dre-estrutura') },
      { key: 'cont-mapeamento', label: 'Mapeamento contábil', path: '/contabilidade/mapeamento-contabil', modulo: 'mod_09', permissao: leituraDaTela('cont-mapeamento') },
      // [ADR-061 D6/D7, US 1045] Lançamento manual — mod_09.
      { key: 'cont-lancamentos', label: 'Lançamentos', path: '/contabilidade/lancamentos', modulo: 'mod_09', permissao: leituraDaTela('cont-lancamentos') },
    ],
  },
  {
    // Minha conta logo depois dos módulos operacionais e antes de
    // Configurações: hierarquia operação > conta > config.
    key: 'minha-conta',
    label: 'Minha conta',
    icon: IconUserCircle,
    // [F4.3 #890] "Meu perfil" e "Chamados" continuam sem `permissao` de propósito —
    // globais (TELAS_GLOBAIS em telas-acesso.ts), visíveis a qualquer usuário autenticado.
    children: [
      { key: 'mc-perfil', label: 'Meu perfil', path: '/minha-conta/perfil' },
      { key: 'mc-cha', label: 'Chamados', path: '/minha-conta/chamados' },
      // [UI-ASSINATURA] Item único — as subtelas (Pagamentos, Forma de
      // pagamento, Notas fiscais) ficam acessíveis por links DENTRO de
      // /minha-conta/assinatura, não no nav.
      // [Decisão de produto 2026-09-28 / ADR-069 §13.7] Deixou de ser global: exige
      // config.assinatura:ler — por padrão só o Administrador (Gestor perdeu por padrão,
      // continua concedível por clone).
      { key: 'mc-ass', label: 'Assinatura', path: '/minha-conta/assinatura', permissao: leituraDaTela('mc-ass') },
    ],
  },
  {
    key: 'configuracoes',
    label: 'Configurações',
    icon: IconSettings,
    children: [
      { key: 'cfg-usu', label: 'Usuários', path: '/settings/users', permissao: leituraDaTela('cfg-usu') },
      // [ADR-069 / F4.1 #910 / F4.3 #890] Enforce no backend só chega na F5 (sem
      // gate real ainda) — o gate AQUI é só UX (esconder o item pra quem não tem a
      // permissão), não proteção.
      { key: 'cfg-perfis', label: 'Perfis e Acesso', path: '/settings/perfis', permissao: leituraDaTela('cfg-perfis') },
      { key: 'cfg-est', label: 'Estabelecimentos', path: '/settings/estabelecimentos', permissao: leituraDaTela('cfg-est') },
      // [MOD-07 AB#300] Fornecedores/Tipos migraram para o menu "Catálogo".
      { key: 'cfg-emp', label: 'Dados cadastrais', path: '/settings/empresa', permissao: leituraDaTela('cfg-emp') },
      // [AB#1275] Só aparece com um módulo de integração ativo — hoje só mod_11
      // (PEP Unimed). `modulo` no filho, mesmo padrão de Caixas de entrada/CRM acima.
      {
        key: 'cfg-integracoes',
        label: 'Integrações',
        path: '/settings/integracoes',
        modulo: 'mod_11',
        permissao: leituraDaTela('cfg-integracoes'),
      },
      // [AB#450] Caixas de entrada, CRM (config), Automações e Respostas
      // rápidas saíram daqui para a seção "Atendimento" (abaixo). Só o
      // AGRUPAMENTO mudou — as rotas continuam em /settings/*, então deep links
      // salvos seguem válidos e nenhum <Navigate> foi necessário.
      // [MOD-03 AB#264] Config de campanhas e Opt-outs saíram daqui — viraram
      // tabs do guarda-chuva /comercial/campanhas (acessível pelo nav primário
      // Comercial → Campanhas). As rotas antigas redirecionam p/ as tabs.
    ],
  },
  /**
   * [AB#450] Configuração do atendimento, agrupada. Antes estes quatro ficavam
   * diluídos em "Configurações", ao lado de Usuários e Dados cadastrais — que
   * são de outra natureza.
   *
   * Todos são `mod_03` e o `modulo` fica em CADA filho, não no grupo: o
   * `AppShell` esconde o item-grupo quando todos os filhos somem pelo gate, de
   * modo que um tenant sem o módulo não vê a seção inteira de qualquer forma.
   * Repetir o gate no pai só duplicaria a regra.
   */
  {
    key: 'atendimento',
    label: 'Atendimento',
    icon: IconHeadset,
    children: [
      // [MOD-03 AB#117] Caixas de entrada (canais). A rota continua em
      // /settings/* porque o usuário pensa "configurar canal"; a operação do
      // dia a dia fica no nav primário Comercial → Inbox.
      // [AB#492] `modulo` em cada filho — subitem não herda o gate do pai.
      {
        key: 'cfg-cmc',
        label: 'Caixas de entrada',
        path: '/settings/comercial/caixas',
        modulo: 'mod_03',
        permissao: leituraDaTela('cfg-cmc'),
      },
      // [MOD-03 AB#132] Config do CRM: estágios do pipeline + campos
      // customizados. Só ADMIN_TENANT abre — backend gating @Modulo('mod_03').
      { key: 'cfg-crm', label: 'CRM', path: '/settings/comercial/crm', modulo: 'mod_03', permissao: leituraDaTela('cfg-crm') },
      // [MOD-03 AB#239 / ADR-038 / #890 item 1, 2026-09-28] UI central de automações de
      // mensagem — ganhou recurso próprio no catálogo (atendimento.automacao).
      {
        key: 'cfg-automacoes',
        label: 'Automações',
        path: '/settings/automacoes',
        modulo: 'mod_03',
        permissao: leituraDaTela('atd-automacoes'),
      },
      // [MOD-03 AB#449 / ADR-045 / #890 item 1, 2026-09-28] Respostas rápidas do inbox —
      // ganhou recurso próprio (atendimento.resposta_rapida).
      {
        key: 'cfg-respostas-rapidas',
        label: 'Respostas rápidas',
        path: '/settings/automacoes/templates-atendimento',
        modulo: 'mod_03',
        permissao: leituraDaTela('atd-respostas-rapidas'),
      },
    ],
  },
  {
    key: 'auditoria',
    label: 'Auditoria',
    icon: IconClipboardList,
    path: '/audit-log',
    permissao: leituraDaTela('auditoria'),
  },
  {
    key: 'faq',
    label: 'Base de conhecimento',
    icon: IconHelpCircle,
    path: '/faq',
  },
];

/* eslint-disable @typescript-eslint/no-unused-vars */
// Atalhos pra ícones reusados em telas (ex.: dashboard cards). Reexportamos
// pra evitar `import { Icon… } from '@tabler/icons-react'` espalhado.
export {
  IconAddressBook,
  IconAlertTriangle,
  IconBell,
  IconBuildingStore,
  IconFileDescription,
  IconFileInvoice,
  IconListDetails,
  IconMessageDots,
  IconPackage,
  IconStack2,
  IconUserShield,
};
